import { useEffect, useState, useRef } from 'react';
import { getApiUrl } from '@/config';

interface Assignment {
    id: string;
    clientName: string;
    startDate: string;
    task: string;
    status: string;
}

// Convert VAPID key helper
function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const useAssignmentNotifications = (technicianId: string | undefined, enabled: boolean) => {
    const [newAssignments, setNewAssignments] = useState<Assignment[]>([]);
    const knownAssignmentsRef = useRef<Set<string>>(new Set());
    const isFirstLoadRef = useRef(true);

    // Subscribe User to Web Push
    const subscribeToPush = async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        if (!technicianId) return;

        try {
            // Register dedicated service worker for push
            const register = await navigator.serviceWorker.register('/sw.js', {
                scope: '/'
            });

            // Check existing subscription
            let subscription = await register.pushManager.getSubscription();

            // If not subscribed, create new subscription
            if (!subscription) {
                const publicVapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
                if (!publicVapidKey) {
                    console.log('No VAPID Key found in environment variables');
                    return;
                }

                subscription = await register.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
                });
            }

            // ALWAYS Send subscription to backend to ensure it's up to date
            // (Even if it existed locally, the server might have lost it or it might be stale)
            if (subscription) {
                const token = localStorage.getItem('token');
                await fetch(getApiUrl('/api/subscribe'), {
                    method: 'POST',
                    body: JSON.stringify({ technicianId, subscription }),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                console.log('✅ Push Notification Subscription Sent to Server');
            }
        } catch (e) {
            console.error('Push Subscription Error', e);
        }
    };

    useEffect(() => {
        if (!enabled || !technicianId) return;

        const checkForNewAssignments = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers: Record<string, string> = {};
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const res = await fetch(getApiUrl('/api/technicians'), { headers });
                if (!res.ok) return;

                const technicians = await res.json();
                const technician = technicians.find((t: any) => t.id === technicianId);

                if (!technician || !technician.assignments) return;

                const currentAssignments = technician.assignments as Assignment[];

                if (isFirstLoadRef.current) {
                    // First load: just memorize all existing IDs, don't notify
                    currentAssignments.forEach(a => knownAssignmentsRef.current.add(a.id));
                    isFirstLoadRef.current = false;
                    return;
                }

                // Subsequent polls: check for IDs we haven't seen before
                const newOnes = currentAssignments.filter(a => !knownAssignmentsRef.current.has(a.id));

                if (newOnes.length > 0) {
                    // Add to known set to avoid repeating notification
                    newOnes.forEach(a => knownAssignmentsRef.current.add(a.id));

                    // Trigger App Notification (Toast)
                    setNewAssignments(prev => [...prev, ...newOnes]);

                    // Trigger Browser System Notification locally (Redundancy/Fallback)
                    // If Service Worker also receives a Push, the 'tag' will prevent duplicate display on some browsers,
                    // or both might show. Better double than nothing.
                    if ('Notification' in window && Notification.permission === 'granted') {
                        newOnes.forEach((assignment) => {
                            try {
                                new Notification('Nouvelle intervention', {
                                    body: `${assignment.clientName} - ${assignment.task}`,
                                    icon: '/pwa-192x192.png',
                                    tag: assignment.id, // Prevents duplicates if SW also fires
                                });
                            } catch (e) {
                                console.error('Notification error', e);
                            }
                        });
                    }
                }
            } catch (error) {
                console.error('Error checking for new assignments:', error);
            }
        };

        // Poll every 10 seconds for faster updates
        const interval = setInterval(checkForNewAssignments, 10000);

        // Immediate check on mount
        checkForNewAssignments();

        return () => clearInterval(interval);
    }, [technicianId, enabled]);

    const dismissNotification = (assignmentId: string) => {
        setNewAssignments(prev => prev.filter(a => a.id !== assignmentId));
    };

    const requestNotificationPermission = async () => {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                await subscribeToPush();
            }
        }
    };

    return {
        newAssignments,
        dismissNotification,
        requestNotificationPermission,
        hasNotifications: newAssignments.length > 0
    };
};
